import { $t } from '~/i18n.js'


export const encodeSecretToSHA256 = async (secret) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(secret);
  const hash = await window.crypto.subtle.digest("SHA-256", data);
  
  // Convert ArrayBuffer to Base64 string
  const hashArray = Array.from(new Uint8Array(hash));
  const hashBase64 = btoa(String.fromCharCode(...hashArray));
  
  return hashBase64;
};


export const updateIfNeeded = (dateLastRun, onUpdate, onSuccess = null, timeDelta = 60000) => {
  // Convenience function used if you want to update only if last_run is bit time ago. Run onSuccess if you return data
  // If no internet then we possibly don't need to run, unless there is no dateLastRun in which case we must
  // (with no baseData it makes no sense to have offline functionality)
  // timeDelta in milliseconds

  // date can be a string in case of persist date last updated
  let delta = -1
  if (!dateLastRun) {
    delta = timeDelta + 1
    // no dateLastRun found -- we must have information initially to run, old is ok, but no data is not ok
  } else if (typeof dateLastRun === 'string' || dateLastRun instanceof String) {
    delta = (new Date()) - new Date(dateLastRun)
  } else {
    delta = (new Date()) - dateLastRun
  }

  if (navigator.onLine) {
    if (delta > timeDelta) {
      onUpdate().then((data) => {
        // only run if success and data
        if (onSuccess && data) {
          onSuccess(data)
        }
      })
    } else {
      onSuccess && onSuccess()
    }
  }
}

export const delay = (s) => new Promise(res => setTimeout(res, s * 1000))
// Use in async function, await delay(5) for 5 seconds

export const selectElementByID = (() => {
  const cache = new WeakMap()
  const CACHE_THRESHOLD = 20 // Cache for lists larger than this

  return (list, field, matchValue) => {
    // Early returns for invalid inputs
    if (matchValue !== 0 && !matchValue) return null
    if (!list?.length) return null

    // For small lists, use simple find (faster than cache overhead)
    if (list.length < CACHE_THRESHOLD) {
      return list.find(item => item && item[field] === matchValue) || null
    }

    // For larger lists, use cached map
    let fieldCache = cache.get(list)
    if (!fieldCache) {
      fieldCache = new Map()
      cache.set(list, fieldCache)
    }

    let indexMap = fieldCache.get(field)
    if (!indexMap) {
      indexMap = new Map()
      list.forEach(item => {
        if (item && item[field] !== undefined) {
          indexMap.set(item[field], item)
        }
      })
      fieldCache.set(field, indexMap)
    }

    return indexMap.get(matchValue) || null
  }
})()

export const generate_uuid = () => {
  // randomize uuid with date-component, of max length 30. Not fully RFC compliant but fast and easy
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}

export const normFileDuringFormUpload = e => {
  // Helper function to retrieve file from form upload
  if (Array.isArray(e)) {
    return e
  }
  return e && e.fileList
}

export const getURLparams = () => {
  const request = {}
  const pairs = window.location.search.substring(window.location.search.indexOf('?') + 1).split('&')
  for (let i = 0; i < pairs.length; i++) {
    if (!pairs[i]) { continue }
    const pair = pairs[i].split('=')
    if (pair[1]) {
      try {
        request[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1])
      } catch (error) {
        console.log('error in decoding uri: ', pair)
      }
    }
  }
  return request
}

export const validateEmail = (email) => {
  // simple email validator <word>@<domain>.<toplevel>
  const res = /\S+@\S+\.\S+/
  return res.test(email)
}

export const ruleCheckImageMaxSize = () => ({
  validator(ruleObject, files) {
    if (!files?.length) return Promise.resolve()
    if (checkFileSize(files, 4)) {
      return Promise.resolve()
    } else {
      return Promise.reject($t("Toegevoegde foto's mogen maximaal ${maxmb} MB zijn, selecteer (op je telefoon) een kleiner data formaat.", { maxmb: 4 }))
    }
  }
})

export const ruleCheckSingleDocument = () => ({
  validator(ruleObject, file) {
    if (file === null) return Promise.resolve()
    if (checkFileSize([file], 15)) {
      return Promise.resolve()
    } else {
      return Promise.reject($t("Toegevoegde bestanden mogen maximaal ${maxmb} MB zijn.", { maxmb: 15 }))
    }
  }
})

export const ruleCheckLogoMaxSize = () => ({
  validator(ruleObject, file) {
    // validator for form object to verify that images are of max size
    // other file types are OK
    if (file === null) return Promise.resolve()
    if (checkFileSize([file], 0.5)) {
      return Promise.resolve()
    } else {
      return Promise.reject($t("Toegevoegde foto's mogen maximaal ${maxmb} MB zijn, selecteer (op je telefoon) een kleiner data formaat.", { maxmb: 0.5 }))
    }
  }
})

const checkFileSize = (files, maxMB) => {
  // Note only check file size for images, this way you can upload large pdf files
  const max = maxMB * 1000000
  let valid = true
  files.forEach(f => {
    if (['image/jpeg', 'image/png', 'image/gif'].indexOf(f.type) >= 0) {
      if (f.size > max) {
        valid = false
      }
    }
  })
  return valid
}

export const formatMessage = (content, data) => {
  // Content parser based on double {{ }} data
  // data can be club.date

  // Create an array of variables that are found in the content using regular expressions
  content = content || ''
  const regex = /{{2}\s*((\w|\.)+)\s*}{2}/g
  const match = content.match(regex)
  match && match.forEach((variablePath) => {
    // for each match, replace the match in the content with the data found in data, e.g. {{domain.name}}
    const path = variablePath.substring(2, variablePath.length - 2).trim()

    // Then perform a walk down data, to achieve the final data value, nested in data
    let replace = data
    path.substring().split('.').forEach((part) => {
      if (part in replace) {
        replace = replace[part]
      }
      return ''
    })
    if (typeof replace === 'string' || typeof replace === 'number') {
      // Only if a string is found, replace data globally
      content = content.replace(variablePath, replace)
    }
  })
  return content
}
